import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Stack, Button, Skeleton, Checkbox, IconButton, Tooltip,
  LinearProgress, Divider, alpha,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  Check, ContentCopy, FileDownloadOutlined, ArrowForward, LockOutlined,
} from '@mui/icons-material';
import { Navigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useCopyLink } from '../hooks/useCopyLink';

import {
  publicApplyService, PublicCycleMetadata, PublicFormLink, SubmitPublicApplicationPayload,
  SubmitPublicApplicationResponse,
} from '../services/admissions/publicApplyService';
import { toUploadUrl } from '../utils/uploadUrl';
import dateTimeUtils from '../utils/dateTimeUtils';
import DynamicFieldRenderer from '../components/admissions/form/DynamicFieldRenderer';
import {
  activeFormSections, splitFormValues, validateDynamicFields, ON_FILE_FAMILY_FIELDS,
} from '../components/admissions/form/admissionFormFields';
import { formatCnic } from '../utils/cnic';
import PublicShell, { HeroFact } from '../components/admissions/public/PublicShell';
import DocumentChecklistInput, { documentFileProblem } from '../components/admissions/form/DocumentChecklistInput';
import { statusLinkService } from '../services/admissions/statusLinkService';
import { personPhotoService } from '../services/personPhotoService';
import { formatMoneyFull } from '../components/reports/finance/chartConfig';
import { useDobHint } from '../components/admissions/form/useDobHint';
import { pathwayFor } from '../utils/admissionAssessment';

/**
 * Public, no-auth admission application at /apply/:token (the cycle's
 * publicToken) — or, with `link`, at /form/:token: a family's own form link
 * from the admissions desk. There the CNICs the desk confirmed are shown
 * locked, the email and class arrive filled in, and once submitted the link
 * leads to the application's status page.
 *
 * The form itself: Parents fill the cycle's own form (cycle.formTemplate —
 * the same snapshot staff configure), confirm a declaration, and submit;
 * the backend creates Family → Person → Application atomically.
 *
 * UX:
 *   • School-branded hero so the parent knows exactly who is asking.
 *   • One scrolling page of numbered sections — admissions forms are long
 *     but linear, and parents mostly fill them on a phone.
 *   • Each section ticks itself off when its required answers are in; a
 *     sticky bar shows overall progress next to the submit button.
 *   • Fail-closed on the token: invalid / closed links get a clear message,
 *     never a half-usable form.
 */

/** Friendly subtitles for the built-in sections, recognised by the system
 *  field they contain (section keys vary between templates). Custom
 *  sections show none. */
const SECTION_HINTS: Array<{ field: string; hint: string }> = [
  { field: 'name',       hint: 'About the child who is applying' },
  { field: 'classId',    hint: 'The class you are applying for and previous schooling' },
  { field: 'fatherName', hint: 'The main contact for the school' },
  { field: 'motherName', hint: 'Optional, but helps us reach the family' },
];
const sectionHint = (fieldKeys: string[]): string | undefined =>
  SECTION_HINTS.find(h => fieldKeys.includes(h.field))?.hint;

type LinkPrefill = Extract<PublicFormLink, { state: 'open' }>['prefill'];
/** A form link that isn't the form: already submitted, cancelled, or the admission closed. */
type LinkElsewhere = Exclude<PublicFormLink, { state: 'open' }>;

const PublicApplyPage: React.FC<{ link?: boolean }> = ({ link = false }) => {
  const { token = '' } = useParams<{ token: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [cycle, setCycle] = useState<PublicCycleMetadata | null>(null);
  const [prefill, setPrefill] = useState<LinkPrefill | null>(null);
  const [elsewhere, setElsewhere] = useState<LinkElsewhere | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** The CNIC is already on file — a former student keeps their GR number. */
  const [duplicate, setDuplicate] = useState<{ grNumber: string | null } | null>(null);
  const [success, setSuccess] = useState<SubmitPublicApplicationResponse | null>(null);

  const [values, setValues] = useState<Record<string, any>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [declaration, setDeclaration] = useState(false);
  const [declarationNudge, setDeclarationNudge] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  // Documents the school asked for (the cycle's checklist), uploaded right
  // after the application is created.
  const [docFiles, setDocFiles] = useState<Record<string, File | null>>({});
  const [docsTouched, setDocsTouched] = useState(false);
  const [docUploadFailures, setDocUploadFailures] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (link) {
          const l = await publicApplyService.getFormLink(token);
          if (cancelled) return;
          if (l.state !== 'open') { setElsewhere(l); return; }
          setCycle(l.cycle);
          setPrefill(l.prefill);
          // What the desk already knows, to save the family typing it.
          setValues({
            ...(l.prefill.email ? { email: l.prefill.email } : {}),
            ...(l.prefill.classId ? { classId: l.prefill.classId } : {}),
            ...(l.prefill.name ? { name: l.prefill.name } : {}),
          });
        } else {
          const c = await publicApplyService.getCycle(token);
          if (!cancelled) setCycle(c);
        }
      } catch (err: any) {
        if (!cancelled) setLoadError(err?.message || 'This admission link is invalid or no longer active.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, link]);

  // On a form link the CNICs were confirmed at the desk, and someone already
  // on file isn't asked for their family again.
  const sections = useMemo(() => {
    if (!cycle) return [];
    const hidden = prefill ? new Set<string>(['cnic', 'fatherCnic', ...(prefill.returning ? Array.from(ON_FILE_FAMILY_FIELDS) : [])]) : undefined;
    return activeFormSections(cycle.formTemplate.sections, hidden);
  }, [cycle, prefill]);

  const classOptions = useMemo(
    () => (cycle?.allowedClassIds || []).map(c => (typeof c === 'object' ? c : { _id: c, name: String(c) })),
    [cycle],
  );
  const dobHint = useDobHint(cycle?.eligibility, classOptions, values.classId, values.dob);


  const errors = useMemo(() => (cycle ? validateDynamicFields(values, sections) : {}), [cycle, sections, values]);

  // The chosen class's documents; before a class is picked, the main list.
  const checklist = useMemo(() => (cycle ? pathwayFor(cycle, values.classId).documentChecklist || [] : []), [cycle, values.classId]);
  const docErrors = useMemo(() => {
    const out: Record<string, string> = {};
    for (const item of checklist) {
      const file = docFiles[item.key];
      if (file) {
        const problem = documentFileProblem(file, item);
        if (problem) out[item.key] = problem;
      } else if (item.required) {
        out[item.key] = `Please attach the ${item.label}.`;
      }
    }
    return out;
  }, [checklist, docFiles]);

  // Progress — required answers given, overall and per section.
  const progress = useMemo(() => {
    const required = sections.flatMap(s => s.fields.filter(f => f.required));
    const requiredDocs = checklist.filter(d => d.required);
    const done = required.filter(f => !errors[f.key]).length + requiredDocs.filter(d => !docErrors[d.key]).length;
    const sectionDone: Record<string, boolean> = {};
    for (const s of sections) {
      const req = s.fields.filter(f => f.required);
      sectionDone[s.key] = req.length > 0 && req.every(f => !errors[f.key]);
    }
    return { total: required.length + requiredDocs.length, done, sectionDone };
  }, [sections, errors, checklist, docErrors]);

  const submit = async () => {
    const all: Record<string, boolean> = {};
    for (const s of sections) for (const f of s.fields) all[f.key] = true;
    setTouched(all);

    const firstKey = sections.flatMap(s => s.fields.map(f => f.key)).find(k => errors[k]);
    if (firstKey) {
      formRef.current
        ?.querySelector(`[data-field-key="${firstKey}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const n = Object.keys(errors).length;
      enqueueSnackbar(`${n} answer${n === 1 ? ' needs' : 's need'} your attention`, { variant: 'warning' });
      return;
    }
    setDocsTouched(true);
    const firstDoc = checklist.find(d => docErrors[d.key]);
    if (firstDoc) {
      formRef.current?.querySelector(`[data-field-key="doc:${firstDoc.key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      enqueueSnackbar(docErrors[firstDoc.key]!, { variant: 'warning' });
      return;
    }
    if (!declaration) {
      setDeclarationNudge(true);
      formRef.current?.querySelector('[data-declaration]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true);
    try {
      const { topLevel, formData } = splitFormValues(values, { sections });
      const payload: SubmitPublicApplicationPayload = {
        ...(topLevel as any),
        formData: Object.keys(formData).length > 0 ? formData : undefined,
      } as SubmitPublicApplicationPayload;
      const result = link
        ? await publicApplyService.submitFormLink(token, payload)
        : await publicApplyService.submit(token, payload);
      // The application exists now; a failed upload must not lose it — the
      // family can upload anything that didn't go through from the status page.
      const failures: string[] = [];
      // An ineligible application is closed — nothing to attach documents to.
      for (const item of result.status === 'Ineligible' ? [] : checklist) {
        const file = docFiles[item.key];
        if (!file) continue;
        try {
          await statusLinkService.uploadDocument(result.statusToken, item.key, file);
        } catch {
          failures.push(item.label);
        }
      }
      if (values.photo instanceof File && result.status !== 'Ineligible') {
        try {
          await personPhotoService.upload({ statusToken: result.statusToken }, values.photo);
        } catch {
          failures.push("Applicant's photo");
        }
      }
      setDocUploadFailures(failures);
      setSuccess(result);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      if (err?.data?.duplicate) setDuplicate({ grNumber: err.data.grNumber });
      else enqueueSnackbar(err?.message || 'Submission failed. Please try again.', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Guards ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <PublicShell>
        <Box sx={{ p: { xs: 3, md: 5 } }}>
          <Skeleton width="40%" height={36} />
          <Skeleton width="70%" />
          <Skeleton variant="rectangular" height={260} sx={{ mt: 3, borderRadius: 2 }} />
        </Box>
      </PublicShell>
    );
  }
  if (elsewhere?.state === 'submitted' && elsewhere.statusToken) {
    // Submitted: the family's link now leads to their application.
    return <Navigate to={`/status/${elsewhere.statusToken}`} replace />;
  }
  if (elsewhere) {
    const message = elsewhere.state === 'cancelled'
      ? { title: 'This link is no longer active', body: 'The admissions office cancelled this form link. Please contact them for a new one.' }
      : elsewhere.state === 'closed'
        ? { title: 'Not taking applications right now', body: `${elsewhere.admission || 'This admission'} isn’t taking applications at the moment. Please contact the admissions office.` }
        : { title: 'Already submitted', body: 'This form has been submitted. Please contact the admissions office for your status link.' };
    return (
      <PublicShell width="narrow" title="Admission form" eyebrow="Admissions" school={'school' in elsewhere ? elsewhere.school : undefined}>
        <MessageCard title={message.title} body={message.body} />
      </PublicShell>
    );
  }
  if (loadError || !cycle) {
    return (
      <PublicShell width="narrow" title="Link not available" eyebrow="Admissions">
        <MessageCard
          title="This link isn’t active"
          body={`${loadError || 'This admission link is invalid or no longer active.'} Please contact the admissions office for a current link.`}
        />
      </PublicShell>
    );
  }

  const fee = cycle.applicationFeeAmount > 0 ? formatMoneyFull(cycle.applicationFeeAmount, 'PKR') : null;
  const shellProps = { school: cycle.school, eyebrow: 'Admissions', title: cycle.name };

  if (success) {
    return (
      <PublicShell {...shellProps} eyebrow="Application received" width="narrow">
        <SuccessCard
          statusToken={success.statusToken}
          applicationNumber={success.applicationNumber}
          fee={success.status === 'Ineligible' ? null : fee}
          ineligible={success.status === 'Ineligible'}
          failedUploads={docUploadFailures}
        />
      </PublicShell>
    );
  }
  if (duplicate) {
    return (
      <PublicShell {...shellProps} width="narrow">
        <MessageCard
          title="You’re already on file"
          body={`A record with this CNIC / B-Form number already exists at ${cycle.school?.name || 'the school'}${duplicate.grNumber ? ` (GR ${duplicate.grNumber})` : ''}. Please contact the admissions office to apply again or update the existing record.`}
          action={<Button variant="outlined" onClick={() => setDuplicate(null)}>Back to the form</Button>}
        />
      </PublicShell>
    );
  }
  // Not taking applications — said the way the server judged it (status,
  // the online switch, the dates on the school's calendar), or every class
  // has stopped taking applications.
  // A form link follows the desk, which the server already checked.
  const notOpen = !link && (cycle.onlineState !== 'open' || classOptions.length === 0);
  if (notOpen) {
    const message =
      cycle.onlineState === 'not_yet' ? {
        title: 'Applications open soon',
        body: `Applications for ${cycle.name} open on ${dateTimeUtils.formatDateForDisplay(cycle.schedule.applicationOpen)}. Please come back then.`,
      }
      : cycle.onlineState === 'paused' ? {
        title: 'Applications are paused',
        body: `Online applications for ${cycle.name} are paused at the moment. Please contact the admissions office — you may still be able to apply in person.`,
      }
      : cycle.onlineState === 'past_deadline' ? {
        title: 'Applications have closed',
        body: `The last date to apply online for ${cycle.name} was ${dateTimeUtils.formatDateForDisplay(cycle.schedule.applicationClose!)}. Please contact the admissions office — late applications may still be taken in person.`,
      }
      : cycle.onlineState === 'open' ? {
        title: 'No places open right now',
        body: `No class in ${cycle.name} is taking applications at the moment. Please contact the admissions office.`,
      }
      : {
        title: 'Applications are closed',
        body: `Applications for ${cycle.name} are not being accepted right now. Please contact the admissions office.`,
      };
    return (
      <PublicShell {...shellProps} width="narrow">
        <MessageCard title={message.title} body={message.body} />
      </PublicShell>
    );
  }

  // ─── Form ────────────────────────────────────────────────────────────
  const daysLeft = cycle.applicationWindow.daysLeft;
  const facts: HeroFact[] = [
    // Rolling admissions have no last date.
    ...(cycle.schedule.applicationClose ? [{
      label: 'Last date to apply',
      value: `${dateTimeUtils.formatDateForDisplay(cycle.schedule.applicationClose)}${daysLeft != null && daysLeft >= 0 ? `  ·  ${daysLeft === 0 ? 'today' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}` : ''}`,
      highlight: daysLeft != null && daysLeft <= 7,
    }] : [{ label: 'Admissions', value: 'Open all year' }]),
    ...(cycle.schedule.classesBeginOn
      ? [{ label: 'Classes begin', value: dateTimeUtils.formatDateForDisplay(cycle.schedule.classesBeginOn) }]
      : []),
    { label: 'Application fee', value: fee ?? 'No fee' },
  ];
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 100;

  return (
    <PublicShell
      {...shellProps}
      eyebrow={link ? 'Your admission form' : 'Admissions open'}
      description={link
        ? `Your own form for ${cycle.name}. Fill it in and submit it; the admissions office receives it straight away.`
        : cycle.description || 'Complete the form below to apply. It takes about five minutes; the admissions office will contact you with next steps.'}
      facts={facts}
      actions={cycle.prospectusFilename ? (
        <Button
          size="small"
          startIcon={<FileDownloadOutlined fontSize="small" />}
          component="a"
          href={toUploadUrl('admissions/prospectus', cycle.prospectusFilename)}
          target="_blank"
          rel="noreferrer"
          sx={{
            color: '#fff', px: 1.5, borderRadius: 5,
            border: `1px solid ${alpha('#fff', 0.3)}`,
            '&:hover': { bgcolor: alpha('#fff', 0.1), borderColor: alpha('#fff', 0.5) },
          }}
        >
          Prospectus
        </Button>
      ) : undefined}
    >
      <Box ref={formRef}>
        {prefill && <ConfirmedIdentity prefill={prefill} />}
        {sections.map((section, i) => (
          <Box key={section.key} sx={{ px: { xs: 2.5, md: 5 }, py: { xs: 3, md: 4 }, borderTop: i ? 1 : 0, borderColor: 'divider' }}>
            <SectionHeader
              index={i + 1}
              title={section.title}
              hint={sectionHint(section.fields.map(f => f.key))}
              done={progress.sectionDone[section.key]}
            />
            <DynamicFieldRenderer
              sections={[section]}
              values={values}
              errors={errors}
              touched={touched}
              onChange={(k, v) => setValues(prev => ({ ...prev, [k]: v }))}
              onBlur={(k) => setTouched(prev => ({ ...prev, [k]: true }))}
              classOptions={classOptions}
              fieldHints={dobHint}
              cardSections={false}
              showSectionTitles={false}
              disabled={submitting}
            />
          </Box>
        ))}

        {/* ── Documents (the cycle's checklist) ───────────────────── */}
        {checklist.length > 0 && (
          <Box sx={{ px: { xs: 2.5, md: 5 }, py: { xs: 3, md: 4 }, borderTop: 1, borderColor: 'divider' }}>
            <SectionHeader
              index={sections.length + 1}
              title="Documents"
              hint="Clear photos or scans are fine"
              done={checklist.every(d => !docErrors[d.key])}
            />
            <DocumentChecklistInput
              items={checklist}
              picked={docFiles}
              onPick={(key, file) => setDocFiles(prev => ({ ...prev, [key]: file }))}
              errors={docsTouched ? docErrors : Object.fromEntries(Object.entries(docErrors).filter(([k]) => docFiles[k]))}
              disabled={submitting}
            />
          </Box>
        )}

        {/* ── Declaration ──────────────────────────────────────────── */}
        <Box sx={{ px: { xs: 2.5, md: 5 }, py: { xs: 3, md: 4 }, borderTop: 1, borderColor: 'divider', bgcolor: (t) => alpha(t.palette.primary.main, 0.03) }}>
          <SectionHeader index={sections.length + (checklist.length > 0 ? 2 : 1)} title="Declaration" done={declaration} />
          <Box
            data-declaration
            onClick={() => { if (!submitting) { setDeclaration(d => !d); setDeclarationNudge(false); } }}
            sx={{
              display: 'flex', gap: 1.5, alignItems: 'flex-start', cursor: 'pointer',
              p: 2, borderRadius: '12px', bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: (t) => (declarationNudge && !declaration ? t.palette.error.main : declaration ? alpha(t.palette.primary.main, 0.5) : t.palette.divider),
              transition: 'border-color 120ms ease',
            }}
          >
            <Checkbox checked={declaration} sx={{ p: 0, mt: 0.1 }} disabled={submitting} />
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              I confirm that the information above is accurate to the best of my knowledge, and I
              understand that admission is subject to document verification and the school&apos;s
              admission policy.
            </Typography>
          </Box>
          {declarationNudge && !declaration && (
            <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 1 }}>
              Please confirm the declaration to submit.
            </Typography>
          )}
          {fee && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              After you submit, an application fee invoice of <strong>{fee}</strong> is issued. Your
              status page shows how to pay.
            </Typography>
          )}
        </Box>

        {/* ── Sticky progress + submit ─────────────────────────────── */}
        <Box
          sx={{
            position: 'sticky', bottom: 0, zIndex: 3,
            px: { xs: 2.5, md: 5 }, py: { xs: 1.5, sm: 2 },
            bgcolor: (t) => alpha(t.palette.background.paper, 0.92), backdropFilter: 'blur(8px)',
            borderTop: 1, borderColor: 'divider',
            borderRadius: '0 0 20px 20px',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {progress.done === progress.total
                    ? 'Ready to submit'
                    : <>{progress.done} of {progress.total}<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> required answers</Box></>}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>{pct}%</Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  height: 6, borderRadius: 3, bgcolor: 'divider',
                  '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: pct === 100 ? 'success.main' : 'primary.main' },
                }}
              />
            </Box>
            <Button
              variant="contained"
              disableElevation
              onClick={submit}
              disabled={submitting}
              endIcon={!submitting && <ArrowForward />}
              sx={{
                flexShrink: 0,
                px: { xs: 2.25, sm: 3.5 },
                py: { xs: 1, sm: 1.25 },
                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                minWidth: { sm: 220 },
                boxShadow: (t) => `0 8px 20px -8px ${alpha(t.palette.primary.main, 0.6)}`,
              }}
            >
              {submitting ? 'Submitting…' : 'Submit'}
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{submitting ? '' : '\u00a0application'}</Box>
            </Button>
          </Stack>
        </Box>
      </Box>
    </PublicShell>
  );
};

// ─── Pieces ────────────────────────────────────────────────────────────────

/** On a form link: who the desk confirmed is applying — fixed, so it isn't asked again. */
const ConfirmedIdentity: React.FC<{ prefill: LinkPrefill }> = ({ prefill }) => (
  <Box sx={{ px: { xs: 2.5, md: 5 }, pt: { xs: 3, md: 4 } }}>
    <Box sx={(t) => ({ p: 2, borderRadius: '12px', bgcolor: alpha(t.palette.primary.main, 0.04), border: `1px solid ${alpha(t.palette.primary.main, 0.15)}` })}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
        <LockOutlined sx={{ fontSize: 16, color: 'primary.main' }} />
        <Typography variant="body2" fontWeight={600}>Confirmed by the admissions office</Typography>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">Applicant’s CNIC / B-Form</Typography>
          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{formatCnic(prefill.cnic)}</Typography>
        </Box>
        {prefill.fatherCnic && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Father’s CNIC</Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{formatCnic(prefill.fatherCnic)}</Typography>
          </Box>
        )}
      </Stack>
      {prefill.returning && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
          Your family’s details are already with the school, so they aren’t asked again.
        </Typography>
      )}
    </Box>
  </Box>
);

const SectionHeader: React.FC<{ index: number; title: string; hint?: string; done?: boolean }> = ({
  index, title, hint, done,
}) => (
  <Stack direction="row" spacing={1.75} alignItems="center" sx={{ mb: 2.5 }}>
    <Box
      sx={{
        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.85rem', fontWeight: 700,
        transition: 'all 200ms ease',
        ...(done
          ? { bgcolor: 'success.main', color: 'success.contrastText' }
          : { bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.12), color: 'primary.main' }),
      }}
    >
      {done ? <Check sx={{ fontSize: 18 }} /> : index}
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-0.01em' }}>{title}</Typography>
      {hint && <Typography variant="body2" color="text.secondary">{hint}</Typography>}
    </Box>
  </Stack>
);

const MessageCard: React.FC<{ title: string; body: string; action?: React.ReactNode }> = ({ title, body, action }) => (
  <Box sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>
    <Typography sx={{ fontWeight: 700, fontSize: '1.4rem', letterSpacing: '-0.01em' }}>{title}</Typography>
    <Typography color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>{body}</Typography>
    {action && <Box sx={{ mt: 3 }}>{action}</Box>}
  </Box>
);

const SuccessCard: React.FC<{
  statusToken: string;
  applicationNumber: string;
  fee: string | null;
  /** Submitted, but a hard eligibility rule failed. */
  ineligible?: boolean;
  /** Documents that didn't upload — to be added from the status page. */
  failedUploads?: string[];
}> = ({ statusToken, applicationNumber, fee, ineligible, failedUploads = [] }) => {
  const copyLink = useCopyLink();
  const url = statusLinkService.url(statusToken);
  const copy = () => copyLink(url, 'Status link');
  const steps = ineligible ? [] : [
    ...(fee ? [{ title: `Pay the application fee (${fee})`, body: 'The invoice and payment details are on your status page.' }] : []),
    { title: 'Review by the admissions office', body: 'We check the form and may ask for documents.' },
    { title: 'Assessment', body: 'If the class has an entry test or interview, your admit card appears on the status page.' },
    { title: 'Decision', body: 'You’ll see the outcome on the status page as soon as it is made.' },
  ];

  return (
    <Box sx={{ p: { xs: 3, md: 5 } }}>
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: (t) => alpha(t.palette.success.main, 0.12), color: 'success.main',
          boxShadow: (t) => `0 0 0 8px ${alpha(t.palette.success.main, 0.06)}`,
        }}>
          <Check sx={{ fontSize: 34 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.01em' }}>Thank you — we’ve received your application</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          {ineligible
            ? 'It doesn’t meet the eligibility rules for the class you chose. Your status page explains why — please contact the admissions office to discuss other options.'
            : 'Keep your application number and status link safe.'}
        </Typography>
        {failedUploads.length > 0 && (
          <Typography sx={{ mt: 1.5, color: 'warning.dark' }} variant="body2">
            We couldn’t upload: {failedUploads.join(', ')}. Please add {failedUploads.length === 1 ? 'it' : 'them'} from your status page.
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
          Application number
        </Typography>
        <Typography sx={{ fontFamily: 'monospace', fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 700, letterSpacing: 1, color: 'primary.main' }}>
          {applicationNumber}
        </Typography>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3, p: 1, pl: 2, borderRadius: '12px', border: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <LockOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography sx={{ flex: 1, minWidth: 0, fontFamily: 'monospace', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {url}
        </Typography>
        <Tooltip title="Copy link"><IconButton size="small" onClick={copy}><ContentCopy fontSize="small" /></IconButton></Tooltip>
      </Stack>
      <Button fullWidth variant="contained" size="large" disableElevation href={url} endIcon={<ArrowForward />} sx={{ mt: 1.5, py: 1.25 }}>
        Track my application
      </Button>

      {steps.length > 0 && (
        <>
        <Divider sx={{ my: 4 }} />
        <Typography sx={{ fontWeight: 700, mb: 2 }}>What happens next</Typography>
        <Stack spacing={0}>
          {steps.map((s, i) => (
            <Stack key={s.title} direction="row" spacing={2}>
              <Stack alignItems="center">
                <Box sx={{
                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: 'primary.main',
                }}>
                  {i + 1}
                </Box>
                {i < steps.length - 1 && <Box sx={{ flex: 1, width: 2, bgcolor: 'divider', my: 0.5 }} />}
              </Stack>
              <Box sx={{ pb: i < steps.length - 1 ? 2.5 : 0 }}>
                <Typography variant="body2" fontWeight={600}>{s.title}</Typography>
                <Typography variant="body2" color="text.secondary">{s.body}</Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
        </>
      )}
    </Box>
  );
};

export default PublicApplyPage;
